import React, { useContext, useState, useEffect, useRef } from "react";
import { Flyout, Loading, SectionTitle } from "@wdpr/groot-components";
import Full from "./Full";
import Empty from "./Empty";
import useFetch from "hooks/useFetch";
import usePreventScroll from "hooks/usePreventScroll";
import useKeyboardNav from "hooks/useKeyboardNav";
import useSessionStorage from "hooks/useSessionStorage";
import useRouteChange from "hooks/useRouteChange";
import {
	arrayIsEmpty,
	objectIsEmpty,
	mapString,
	isCurrentPathPresent,
} from "utils";
import { getCookie, setCookie, removeCookie } from "utils/cookie";
import { sendCartToChat } from "utils/cart";
import { buildHref } from "utils/link";
import { Context } from "Context";
import config from "config.json";
import {
	WdprIconLibrary,
	WdprNavItemSmall,
	WdprNavIconButton,
} from "components/vista";
import { defaultIconVariant } from "config-txp";
import { useIconButtonVariant } from "hooks/txp/useIconButtonVariant";
import { useCallback } from "react";
import AnalyticsWrapper from "components/shared/AnalyticsWrapper";

const syndicatedMakeCartRequest = "syndicatedMakeUserCartRequest";
const cartUserIdentifier = "cartUserIdentifier";
const cartIdName = "cartId";
const cartEvent = "navigationCart";
const flyoutPositionLeft = "left";

const Cart = ({
	cartEmpty,
	cartLabel,
	checkOut,
	descriptions = [],
	icon,
	id,
	link,
	price: initialPrice,
	standaloneItemsTitle: otherItems,
	title,
	viewCartCheckOut,
	viewDetails,
	startOver,
	emptyCart,
	accessibilityText,
	cartEmptyText = "empty",
	cartItemsAddedText = "items added",
	isMenuActive,
	setIsMenuActive,
	cartStore = "",
	flyoutPosition = flyoutPositionLeft,
	additionalItems: additionalItemsText,
	isMobileView,
	isLastSection,
	txp = false,
	extraOptions,
	iconVariant,
	setSearchBarToggle,
	isHeaderHovered,
	isScrolling,
	searchBarToggle,
	setHoveredSection,
}) => {
	const {
		config: {
			brand,
			store: initialStoreValue,
			isTxp,
			shouldInvertItems,
			basePath,
			locale,
		},
		profileData: { user },
		cartHasItems: hasItems,
		setCartHasItems: setHasItems,
		cdnUrl,
		cdnAssetsUrl,
	} = useContext(Context);
	const { cart } = extraOptions || {};

	const processHref = (href) =>
		buildHref(href, initialStoreValue, basePath, false);
	const [storageMakeCartRequest, setStorageMakeCartRequest] = useSessionStorage(
		syndicatedMakeCartRequest
	);

	const brandConfig = config.brands?.[brand] || {};
	const store = initialStoreValue || cartStore;
	const isDCL = brand === "dcl";
	const showFlyout = !window?.location.pathname.split("/").includes("cart");
	const [cartTimestamp, setCartTimestamp] = useState(new Date());
	const [detailsUrl, setDetailsUrl] = useState("");
	const [scope] = useState(
		isDCL ? "" : `${brandConfig.cartScope || brand}${store ? `_${store}` : ""}`
	);
	const [items, setItems] = useState([]);
	const [standaloneItems, setStandaloneItems] = useState([]);
	const [compositeItems, setCompositeItems] = useState([]);
	const [standaloneItemsTitle, setStandaloneItemsTitle] = useState("");
	const [price, setPrice] = useState(initialPrice);
	const [unavailableItemsMessage, setUnavailableItemsMessage] = useState({});
	const [currencyChangeDetail, setCurrencyChangeDetail] = useState(null);
	const [cartCookieJarName, setCartCookieJarName] = useState("");
	const [cartCookieJarValue, setCartCookieJarValue] = useState({});
	const [checkoutRefactorValue, setcheckoutRefactorValue] = useState(false);
	const [totalNumberOfItems, setTotalNumberOfItems] = useState(0);
	const [onHover, setOnHover] = useState(false);
	const sectionRef = useRef(null);
	const iconButtonRef = useRef(null);
	const flyoutId = id ? `syndicated-flyout-${id}` : undefined;
	const { onFocus, handleKeyDown, handleOnFocus, handleOnBlur } =
		useKeyboardNav(
			sectionRef,
			isMenuActive,
			setIsMenuActive,
			isLastSection,
			txp
		);

	// Set aria attributes on the shadow DOM elements inside WdprNavIconButton
	useEffect(() => {
		const el = iconButtonRef.current;
		if (!el?.shadowRoot) return;
		const link = el.shadowRoot.querySelector("a");
		const chevron = el.shadowRoot.querySelector("button");
		if (link) {
			link.setAttribute("aria-label", title || "Cart");
		}
		if (chevron) {
			chevron.setAttribute("aria-label", "Toggle menu");
			chevron.removeAttribute("aria-pressed");

			const handleFocus = () => chevron.setAttribute("aria-label", title || "Cart");
			const handleBlur = () => chevron.setAttribute("aria-label", "Toggle menu");

			chevron.addEventListener("focus", handleFocus);
			chevron.addEventListener("blur", handleBlur);

			return () => {
				chevron.removeEventListener("focus", handleFocus);
				chevron.removeEventListener("blur", handleBlur);
			};
		}
	}, [onHover, title]);

	const mainClass = "syndicated-section";
	let classes = `${mainClass} ${mainClass}--${brand}`;
	if (id) classes += ` ${mainClass}--${id}`;
	classes += ` ${mainClass}--${hasItems ? "full" : "empty"}`;
	if (!showFlyout) classes += ` ${mainClass}--no-flyout`;
	if (isMenuActive && onFocus) classes += ` ${mainClass}--onfocus`;
	if (onHover) classes += ` ${mainClass}--onhover`;

	const cartStateText = hasItems
		? `${totalNumberOfItems || ""} ${cartItemsAddedText}`.trim()
		: cartEmptyText;

	const isTradePage = () => {
		return store && store !== cartStore;
	};

	const isUserLoggedIn = () => {
		return !!user?.swid;
	};

	const getHeaders = () => {
		return {
			headers: {
				swid: isTradePage() ? "" : user?.swid || "",
				token: user?.authToken || "",
			},
		};
	};

	const { response, error, loading } = useFetch(
		detailsUrl,
		getHeaders(),
		0,
		detailsUrl
	);

	const getCartCookies = () => {
		const cartCookieName = isDCL
			? "new"
			: user?.swid
			? cartUserIdentifier
			: cartIdName;
		const cartCookieJarName = isDCL ? "cartIdMapping" : `CART-${scope}_jar`;

		setCartCookieJarName(cartCookieJarName);
		let cartCookieJarContent = getCookie(cartCookieJarName);
		const checkoutRefactor = getCookie(brandConfig?.checkoutRefactorCookieName);
		if (checkoutRefactor) {
			setcheckoutRefactorValue(true);
		}
		if (cartCookieJarContent) {
			try {
				cartCookieJarContent = JSON.parse(
					decodeURIComponent(cartCookieJarContent)
				);
				if (
					cartCookieJarContent[cartCookieName] ||
					(user?.swid && cartCookieJarContent[cartIdName])
				) {
					setCartCookieJarValue(cartCookieJarContent);
				}
			} catch (e) {
				resetCartCookie(cartCookieJarName);
				console.info("Error parsing the cart cookie, content is malformed");
			}
		}
	};

	const getTimeMachine = () => {
		let timeMachine = "";
		const timeMachineCookie = getCookie("setSystemTime");
		if (!timeMachineCookie) return "";
		const togglesCookie = getCookie("featureToggles");
		if (!togglesCookie) return "";
		try {
			const timeMachineToggle = JSON.parse(
				decodeURIComponent(togglesCookie)
			).systemTimeOverrideHeader;
			if (timeMachineToggle) {
				timeMachine = timeMachineCookie;
			}
		} catch (error) {
			console.error("Error parsing the featureToggles cookie");
		}
		return timeMachine;
	};

	const setRequestUrls = () => {
		const excludedPaths = config.cart?.excludedPaths || [];
		if (isCurrentPathPresent(excludedPaths)) {
			return;
		}
		const useAnonymousCart = brandConfig.useAnonymousCartOnly;
		const isLoggedIn = useAnonymousCart ? false : isUserLoggedIn();
		const {
			cartId = "",
			new: cartIdDcl = null,
			[cartUserIdentifier]: {
				id = null,
				type = !isDCL && isLoggedIn && !isTradePage() ? "GUEST" : "",
				idType = !isDCL && isLoggedIn && !isTradePage() ? "swid" : "",
			} = {},
		} = cartCookieJarValue;

		// if swid cookie matches the cartUserIdentifier swid cookie of the CART-XX_jar
		const isUserCartCookieSet =
			isLoggedIn && id && type && idType && id === user.swid;
		isUserCartCookieSet && setStorageMakeCartRequest(true);
		const makeRequest =
			isLoggedIn && !isUserCartCookieSet && storageMakeCartRequest;
		const isValidCartDCL = !!(isDCL && cartIdDcl);
		const isValidCartOtherBrands = !!(
			scope &&
			(cartId || isUserCartCookieSet || makeRequest)
		);
		const timeMachine = getTimeMachine();

		if (isValidCartDCL || isValidCartOtherBrands) {
			setDetailsUrl(
				mapString(
					config.cart.apiUrl,
					"details",
					scope,
					isDCL ? cartIdDcl : cartId,
					type,
					idType,
					timeMachine,
					checkoutRefactorValue
				)
			);
		}
	};

	const setUserCartCookie = () => {
		if (
			!brandConfig.preventSettingUserCartCookie &&
			user?.swid &&
			(objectIsEmpty(cartCookieJarValue) || cartCookieJarValue?.[cartIdName])
		) {
			const cookieContent = {
				[cartUserIdentifier]: {
					type: "GUEST",
					id: user?.swid,
					idType: "swid",
				},
			};
			setCookie(
				cartCookieJarName,
				encodeURIComponent(JSON.stringify(cookieContent)),
				config.cookieExpirationInDays
			);
			setCartCookieJarValue(cookieContent);
		}
	};

	const resetCartCookie = (cartCookieName = "") => {
		user?.swid && setStorageMakeCartRequest(false);
		setCartCookieJarValue({});
		if (!checkoutRefactorValue) {
			removeCookie(cartCookieJarName || cartCookieName);
		}
	};

	const resetCart = () => {
		setDetailsUrl("");
		setItems([]);
		setHasItems(false);
		setTotalNumberOfItems(0);
	};

	const cartEventLogic = (event) => {
		if (event?.detail?.method === "reload") {
			resetCart();
			setCartTimestamp(new Date());
		}
	};

	const validateCartStatus = (response) => {
		if (
			(response.status === 404 || response.status === 204) &&
			!isTradePage() &&
			!isUserLoggedIn()
		) {
			resetCartCookie();
		}
	};

	const handleMouseEnter = useCallback(() => {
		if (!searchBarToggle) {
			setOnHover(true);
		}
		// Clear any open navigation section when Cart opens
		setHoveredSection?.(null);
	}, [searchBarToggle, setHoveredSection]);

	const handleMouseLeave = () => {
		setOnHover(false);
	};

	useEffect(() => {
		typeof storageMakeCartRequest === "undefined" &&
			setStorageMakeCartRequest(true);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		getCartCookies();
		if (!brandConfig.useAnonymousCartOnly && !user?.swid && hasItems) {
			resetCart();
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.swid, store, cartTimestamp]);

	useEffect(() => {
		(cartCookieJarValue || user?.swid) && setRequestUrls();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartCookieJarValue, storageMakeCartRequest, user?.swid, hasItems]);

	useRouteChange(() => {
		const excludedPaths = config.cart?.excludedPaths || [];
		if (isCurrentPathPresent(excludedPaths)) {
			resetCart();
		}
	});

	useEffect(() => {
		if (response) {
			if (response.totalNumberOfItems > 0 && !arrayIsEmpty(response.items)) {
				setHasItems(true);
				setTotalNumberOfItems(response.totalNumberOfItems);
				if (!isTradePage()) {
					setUserCartCookie();
				}
				setItems(response.items);
				!objectIsEmpty(response.price) && setPrice(response.price);
				!objectIsEmpty(response.message) &&
					setUnavailableItemsMessage(response.message);
				!objectIsEmpty(response.currencyChangeDetail) &&
					setCurrencyChangeDetail(response.currencyChangeDetail);
				sendCartToChat(
					{
						total: response.price?.total || 0,
						currency: response.price?.currency,
						products: response.items,
					},
					brandConfig?.chatConfiguration?.sendCartInformationPages,
					brandConfig?.chatConfiguration?.sendCartToChat
				);
			}
			validateCartStatus(response);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		if (error) {
			validateCartStatus(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		setCompositeItems(items.filter((item) => !arrayIsEmpty(item?.items)));
		setStandaloneItems(items.filter((item) => !item.items));
	}, [items]);

	useEffect(() => {
		!arrayIsEmpty(standaloneItems) &&
			!arrayIsEmpty(compositeItems) &&
			setStandaloneItemsTitle(otherItems);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [standaloneItems, compositeItems]);

	useEffect(() => {
		document?.addEventListener(cartEvent, cartEventLogic);
		return () => {
			document?.removeEventListener(cartEvent, cartEventLogic);
		};
	});

	// Prevent scroll when hovering in TXP mode
	usePreventScroll(isTxp && onHover && !isMobileView);

	useEffect(() => {
		setSearchBarToggle?.(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const variant = useIconButtonVariant({
		toggle: isHeaderHovered,
		isScrolling,
		brand,
	});

	const inverse = variant === defaultIconVariant;
	const defaultCartIcon = icon || cart?.icon;
	const cartIcon = hasItems ? "cart-full" : defaultCartIcon;

	const cartAccessibilityText = accessibilityText
		? accessibilityText.includes("-")
			? "Cart Press enter to navigate or collapse by pressing escape"
			: accessibilityText
		: title;

	if (isMobileView && txp) {
		return (
			<WdprNavItemSmall
				key={cart?.title}
				label={cart?.title}
				variant={iconVariant}
				href={processHref(cart?.link?.href || "")}
				target={cart?.link?.target}
				inverse={shouldInvertItems}
			>
				{cartIcon && (
					<WdprIconLibrary
						slot="leading-icon"
						icon={cartIcon}
						size="medium"
						decorative
					/>
				)}
			</WdprNavItemSmall>
		);
	}

	return (
		<div
			className={classes}
			tabIndex={isMobileView ? null : -1}
			ref={sectionRef}
			onKeyDown={handleKeyDown}
			onFocus={handleOnFocus}
			onBlur={handleOnBlur}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			role="group"
		>
			{!isTxp ? (
				<SectionTitle
					link={link}
					name={id}
					icon={cartIcon}
					title={title}
					accessibilityText={
						isDCL && locale === "en-us"
							? accessibilityText
								? `${cartStateText} ${accessibilityText}`
								: cartStateText
							: accessibilityText
					}
					ariaLive="polite"
					linkRole="button"
					brand={brand}
					isMenuActive={isMenuActive && onFocus}
					cdnUrl={cdnUrl}
					cdnAssetsUrl={cdnAssetsUrl}
					txp={txp}
					hideTitle={txp && isTxp}
					showFlyout={showFlyout}
					collapse={onHover || isMenuActive}
				/>
			) : (
				<AnalyticsWrapper analyticsLinkId={link?.analyticsLinkId}>
					<WdprNavIconButton
						ref={iconButtonRef}
						href={processHref(link?.href || "")}
						target={link?.target}
						iconName={cartIcon}
						inverse={inverse}
						active={onHover}
						_isOpen={onHover}
						controlsId={flyoutId}
						onWdprNavIconButtonPressed={() => setOnHover((prev) => !prev)}
						onClick={() => setSearchBarToggle?.(false)}
						a11yLabel={cartAccessibilityText}
					/>
				</AnalyticsWrapper>
			)}

			{showFlyout && (
				<Flyout
					collapse={onHover}
					left={flyoutPosition === flyoutPositionLeft}
					name={id}
					id={flyoutId}
				>
					<Flyout.Primary name={id}>
						<div className={`${mainClass}__content`}>
							{!txp && cartLabel && (
								<h2 className={`${mainClass}__content__title`}>{cartLabel}</h2>
							)}
							{loading ? (
								<Loading cdnUrl={cdnUrl} />
							) : hasItems ? (
								<Full
									brand={brand}
									message={unavailableItemsMessage}
									compositeItems={compositeItems}
									standaloneItems={standaloneItems}
									standaloneItemsTitle={standaloneItemsTitle}
									price={price}
									currencyChangeDetail={currencyChangeDetail}
									startOver={startOver}
									emptyCart={emptyCart}
									buttons={{
										checkOut,
										viewCartCheckOut,
										viewDetails,
									}}
									totalNumberOfItems={response?.totalNumberOfItems}
									additionalItemsText={additionalItemsText}
									isTxp={txp}
									isHovering={onHover}
								/>
							) : (
								<Empty
									id={id}
									brand={brand}
									text={cartEmpty}
									descriptions={descriptions || []}
									price={price?.sections || initialPrice?.sections || {}}
									separateFooter={isDCL}
									txp={txp}
								/>
							)}
						</div>
					</Flyout.Primary>
				</Flyout>
			)}
		</div>
	);
};

export default Cart;
